import {AppLinkDto} from '@lib/infrastructure';
import {getAppLink, notFoundErrorName, useAppDispatch} from '@lib/store';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AppLinkErrorType, validateAppLink} from '../util/appLinkUtils';

type AppLinkState =
  | {status: 'Loading' | 'NotLoaded'; appLink: null; error: null}
  | {status: 'Loaded'; appLink: AppLinkDto; error: null}
  | {status: 'Failed'; appLink: null; error: AppLinkErrorType};

export const useAppLinkLoader = (appLinkId: string) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();

  const [appLinkState, setAppLinkState] = useState<AppLinkState>({
    status: 'NotLoaded',
    appLink: null,
    error: null,
  });

  useEffect(() => {
    let isCancelled = false; // Track whether the effect has been cancelled.

    const loadAppLink = async () => {
      setAppLinkState({status: 'Loading', appLink: null, error: null});

      try {
        const apiResponse = await dispatch(getAppLink(appLinkId)).unwrap();

        if (isCancelled) return; // Prevent state update if the effect is cancelled.

        const appLinkValidationeErrors = validateAppLink(apiResponse, t);

        if (appLinkValidationeErrors.length) {
          setAppLinkState({status: 'Failed', appLink: null, error: appLinkValidationeErrors[0]});
          return;
        }

        setAppLinkState({status: 'Loaded', appLink: apiResponse, error: null});
      } catch (err) {
        if (isCancelled) return; // Prevent state update if the effect is cancelled.

        const apiError = err as Error;

        if (apiError.name === notFoundErrorName) {
          setAppLinkState({
            status: 'Failed',
            appLink: null,
            error: {
              title: t('appLinks:NotFoundTitle'),
              message: t('appLinks:NotFoundMessage', {linkId: appLinkId}),
              type: 'notFound',
            },
          });
        } else {
          setAppLinkState({status: 'Failed', appLink: null, error: {message: apiError.message}});
        }
      }
    };

    loadAppLink();

    // Cleanup function to mark the operation as cancelled.
    return () => {
      isCancelled = true;
    };
  }, [appLinkId, dispatch, t]);

  return appLinkState;
};
